import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import AccessRequestDetailPanel from '@devnw/cyber-iam/iam/AccessRequestDetailPanel/AccessRequestDetailPanel'
// eslint-disable-next-line import/no-unresolved
import GrantActivationPanel from '@devnw/cyber-iam/iam/GrantActivationPanel/GrantActivationPanel'
import { useAccessRequestsApi } from '../api/accessRequestsClient'
import type { AccessRequest, EvidenceRecord } from '../api/types'
import { toIamAccessRequest } from '../api/accessRequestMapping'
import { useActiveTenant } from '../layout/TenantSwitcher'
import { useAuth } from '../auth/AuthContext'
import { useGrantActivationCallbacks } from '../auth/useGrantActivationCallbacks'
import { PamBreadcrumbs } from '../components/PamBreadcrumbs'
import { PamLoading, PamError, PamEmpty } from '../components/PamStateComponents'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Failed to load access request'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('md')};
`

const EvidenceLinkRow = styled.div`
  display: flex;
`

const EvidenceLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xxs')} ${ut(asUnified(theme)).space('md')}`};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor()};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius()}px;
  color: ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
  font-weight: 600;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 2px;
  }
`

const EvidenceCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xs')};
  padding: ${({ theme }) => ut(asUnified(theme)).space('md')};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor()};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius()}px;
  background: ${({ theme }) => asUnified(theme).bg?.surface ?? '#ffffff'};
`

const SectionHeading = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text?.secondary ?? '#64748b'};
`

/**
 * OIDCPAM Gate-6 Phase 5a: migrated off MUI onto
 * @devnw/cyber-iam/iam/AccessRequestDetailPanel (approve/deny/cancel/extend)
 * composed with iam/GrantActivationPanel (rendered in the panel's
 * `activationSlot` — the exact composition its own doc comments describe)
 * for the "Activate Grant" step-up chain. This page still owns every API
 * call (get/evidence/approve/deny/cancel/extend/activate), the DPoP-on-
 * activate ceremony (via useGrantActivationCallbacks, unchanged transport),
 * tenant/routing, and the wire-mapping to the library's camelCase shapes.
 */
export const AccessRequestDetail = (): JSX.Element => {
  const { requestId } = useParams<{ requestId: string }>()
  const navigate = useNavigate()
  const { tenant } = useActiveTenant()
  const { claims } = useAuth()
  const accessRequestsApi = useAccessRequestsApi()
  const [req, setReq] = useState<AccessRequest | null>(null)
  const [evidence, setEvidence] = useState<EvidenceRecord | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  // Admin, tenant-scoped activation (origin's ThreadID hardening kept this
  // page on the admin API — only ActivateGrant moved to self-service). The
  // injected callback decouples the shared step-up transport from the API
  // choice, exactly as origin decoupled the retired useActivateWithStepUp.
  const activateRequest = useCallback(async (): Promise<void> => {
    if (!requestId) throw new Error('Missing access request ID')
    await accessRequestsApi.activate(tenant, requestId)
  }, [accessRequestsApi, tenant, requestId])
  const activationCallbacks = useGrantActivationCallbacks(activateRequest)

  const load = (): void => {
    if (!requestId) return
    setLoading(true)
    Promise.all([
      accessRequestsApi.get(tenant, requestId),
      accessRequestsApi
        .evidence(tenant, requestId)
        .catch((): EvidenceRecord | null => null),
    ])
      .then(([r, ev]) => {
        setReq(r)
        setEvidence(ev)
      })
      .catch((e: unknown) => setError(errMessage(e)))
      .finally(() => setLoading(false))
  }
  useEffect(load, [tenant, requestId])

  if (loading) return <PamLoading />
  if (error) return <PamError message={error} />
  if (!req) return <PamEmpty message="Request not found" />

  const idLabel = requestId ? requestId.slice(0, 8) + '...' : '...'
  const canActivate = req.status === 'approved'

  return (
    <PageContainer>
      <PamBreadcrumbs
        crumbs={[
          { label: 'Access Requests', to: '/admin/access-requests' },
          { label: idLabel },
        ]}
      />

      <AccessRequestDetailPanel
        testID="access-request-detail"
        title="Access Request"
        request={toIamAccessRequest(req)}
        currentUserId={claims?.sub}
        onApprove={(rationale) => accessRequestsApi.approve(tenant, requestId ?? '', rationale)}
        onDeny={(rationale) => accessRequestsApi.deny(tenant, requestId ?? '', rationale)}
        onCancel={() => accessRequestsApi.cancel(tenant, requestId ?? '')}
        onExtend={(additionalSeconds) =>
          accessRequestsApi.extend(tenant, requestId ?? '', additionalSeconds)}
        onActionComplete={() => load()}
        activationSlot={
          canActivate ? (
            <GrantActivationPanel
              testID="access-request-activate"
              request={toIamAccessRequest(req)}
              callbacks={activationCallbacks}
              onActivated={() => load()}
              showRequestSummary={false}
              onNavigateToPolicies={(policyLink) => navigate(policyLink)}
            />
          ) : undefined
        }
      />

      <EvidenceLinkRow>
        <EvidenceLink to={`/admin/access-requests/${req.id}/evidence`}>
          View Evidence
        </EvidenceLink>
      </EvidenceLinkRow>

      {evidence && (
        <EvidenceCard>
          <SectionHeading>Evidence Record</SectionHeading>
          <MutedText>
            {evidence.approvals.length} approval decision(s) recorded.
            {evidence.activated_grant
              ? ` Grant activated at ${evidence.activated_grant.created_at}.`
              : ' Not yet activated.'}
          </MutedText>
        </EvidenceCard>
      )}
    </PageContainer>
  )
}
